import { useMemo, useState, useRef } from "react";
import { useColumnLayout } from "@/lib/useColumnLayout";
import { useTableSort } from "@/lib/useTableSort";
import SortLabel from "@/components/SortLabel";
import DataPagination from "@/components/DataPagination";
import { GripVertical } from "lucide-react";
import { cn } from "@/lib/utils";

// Tabella riutilizzabile: intestazioni centrate, ordinabili, filtrabili,
// con ordine colonne trascinabile e larghezza ridimensionabile (salvate per-utente).
export default function DataTable({
  tableKey, columns, rows, rowKey, rowTestId, emptyText = "Nessun dato",
  containerClassName = "", pageSize, initialSort, footer, dense = false,
}) {
  const { orderedColumns, widths, setWidth, moveColumn } = useColumnLayout(tableKey, columns);

  const accessors = useMemo(() => {
    const acc = {};
    columns.forEach((c) => { if (c.sortable) acc[c.id] = c.sortValue || ((r) => r[c.id]); });
    return acc;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const { sorted, sortKey, sortDir, toggleSort } = useTableSort(rows, accessors, initialSort || {});

  const [page, setPage] = useState(1);
  const total = sorted.length;
  const pageCount = pageSize ? (Math.ceil(total / pageSize) || 1) : 1;
  const safePage = Math.min(page, pageCount);
  const visible = pageSize ? sorted.slice((safePage - 1) * pageSize, safePage * pageSize) : sorted;

  const totalWidth = orderedColumns.reduce((s, c) => s + (widths[c.id] || 160), 0);
  const dragId = useRef(null);
  const resizing = useRef(null);

  const onResizeStart = (e, colId) => {
    e.preventDefault();
    e.stopPropagation();
    resizing.current = { colId, startX: e.clientX, startW: widths[colId] || 160 };
    const onMove = (ev) => {
      if (!resizing.current) return;
      const delta = ev.clientX - resizing.current.startX;
      setWidth(resizing.current.colId, resizing.current.startW + delta);
    };
    const onUp = () => {
      resizing.current = null;
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  return (
    <>
      <div className={cn("overflow-auto", containerClassName)}>
        <table className="border-collapse text-sm" style={{ width: totalWidth, minWidth: "100%", tableLayout: "fixed" }}>
          <colgroup>
            {orderedColumns.map((c) => <col key={c.id} style={{ width: widths[c.id] || 160 }} />)}
          </colgroup>
          <thead className="sticky top-0 z-20">
            <tr className="bg-slate-50 border-b border-slate-200">
              {orderedColumns.map((c) => (
                <th
                  key={c.id}
                  data-testid={`col-${tableKey}-${c.id}`}
                  className="relative border-r border-slate-200 select-none"
                  draggable={c.reorderable !== false}
                  onDragStart={() => { dragId.current = c.id; }}
                  onDragOver={(e) => { if (dragId.current && dragId.current !== c.id) e.preventDefault(); }}
                  onDrop={() => { if (dragId.current) { moveColumn(dragId.current, c.id); dragId.current = null; } }}
                >
                  <div className="flex items-center justify-center gap-1 px-2 py-2">
                    {c.reorderable !== false && (
                      <GripVertical data-testid={`col-grip-${tableKey}-${c.id}`} className="h-3 w-3 text-slate-300 cursor-grab flex-shrink-0" />
                    )}
                    {c.sortable ? (
                      <SortLabel label={c.label} active={sortKey === c.id} dir={sortDir}
                        onClick={() => toggleSort(c.id)} testid={`sort-${c.id}`} />
                    ) : (
                      <span className="text-xs font-semibold uppercase tracking-widest text-slate-500 truncate">{c.label}</span>
                    )}
                    {c.filter}
                  </div>
                  {c.resizable !== false && (
                    <span
                      data-testid={`col-resize-${tableKey}-${c.id}`}
                      onMouseDown={(e) => onResizeStart(e, c.id)}
                      className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize hover:bg-blue-400/50"
                    />
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 ? (
              <tr>
                <td colSpan={orderedColumns.length} className="py-14 text-center text-sm text-slate-400">{emptyText}</td>
              </tr>
            ) : (
              visible.map((row) => (
                <tr key={rowKey(row)} data-testid={rowTestId ? rowTestId(row) : undefined} className="hover:bg-slate-50 border-b border-slate-100">
                  {orderedColumns.map((c) => (
                    <td
                      key={c.id}
                      className={cn(
                        "border-r border-slate-100 truncate",
                        dense ? "px-2 py-1" : "px-3 py-2.5",
                        c.align === "right" ? "text-right tabular-nums" : c.align === "center" ? "text-center" : "text-left",
                        typeof c.cellClassName === "function" ? c.cellClassName(row) : c.cellClassName,
                      )}
                    >
                      {c.cell ? c.cell(row) : row[c.id]}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
          {columns.some((c) => c.footer) && (
            <tfoot className="sticky bottom-0 z-10">
              <tr className="bg-slate-100 font-semibold border-t border-slate-200">
                {orderedColumns.map((c) => (
                  <td key={c.id} data-testid={`foot-${tableKey}-${c.id}`}
                    className={cn("border-r border-slate-100 px-3 py-2.5",
                      c.align === "right" ? "text-right tabular-nums" : c.align === "center" ? "text-center" : "text-left")}>
                    {c.footer ? c.footer(sorted) : null}
                  </td>
                ))}
              </tr>
            </tfoot>
          )}
          {footer}
        </table>
      </div>
      {pageSize && total > pageSize && (
        <DataPagination page={safePage} pageCount={pageCount} total={total} onPage={setPage} />
      )}
    </>
  );
}
